// ST-00218 P3: "N tickets probably on the wrong route — review". After a summary has loaded,
// Route Summary asks which tickets of its route(s) and period probably carry the wrong dispatch /
// receipt location (GET /route-summary/location-suggestions, from the load-match ledger) and shows
// one line near the figures; the link opens the list (LocationSuggestionsList, the same as the
// Load matching drawer's "Location probably wrong"). Shown only: every correction goes through
// Amendments. Nothing at all while load matching is off, when there is nothing to suggest or when
// the request fails — the summary never waits for it.
import { useCallback, useEffect, useRef, useState } from 'react';
import { FaMapMarkerAlt, FaTimes } from 'react-icons/fa';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import type { GroupingId } from '../../../types/clientPages/routeSummary';
import type { LocationSuggestionsResponse } from '../../../types/loadMatching';
import LocationSuggestionsList from '../RouteIntelligence/LoadMatching/LocationSuggestionsList';
import { fmtN, fmtPeriod } from '../RouteIntelligence/LoadMatching/format';

export interface LocationSuggestionsIndicatorProps {
  /** Every route of the summary. */
  groupingIds: GroupingId[];
  startDate: string;
  endDate: string;
  /** The summary's route label (one route, or "N routes combined: …"). */
  label: string | null;
}

const indicatorText = (tickets: number, groupings: number): string =>
  `${fmtN(tickets)} ${tickets === 1 ? 'ticket' : 'tickets'} probably on the wrong route — review` +
  (groupings > 0
    ? ` · ${fmtN(groupings)} ${groupings === 1 ? 'route list' : 'route lists'} to check`
    : '');

function SuggestionsDialog({
  data,
  label,
  period,
  canAmend,
  onClose,
}: {
  data: LocationSuggestionsResponse;
  label: string | null;
  period: { startDate: string; endDate: string };
  canAmend: boolean;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="loc-suggestions-title"
        className="w-full sm:max-w-3xl max-h-[92vh] bg-surface rounded-t-xl sm:rounded-xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 sm:px-6 py-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 id="loc-suggestions-title" className="text-base font-bold text-slate-900">
              Tickets probably on the wrong route
            </h3>
            <p className="text-xs text-slate-500 break-words">
              {label ? `${label} · ` : ''}
              {fmtPeriod(period)}
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 text-slate-400 hover:text-slate-600 flex-shrink-0"
          >
            <FaTimes size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          <LocationSuggestionsList panel={data} canAmend={canAmend} />
        </div>
      </div>
    </div>
  );
}

export default function LocationSuggestionsIndicator({
  groupingIds,
  startDate,
  endDate,
  label,
}: LocationSuggestionsIndicatorProps) {
  const { hasPermission } = useAuth();
  const canAmend = hasPermission('amendments.view');
  const [data, setData] = useState<LocationSuggestionsResponse | null>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const idsKey = groupingIds.join(',');

  useEffect(() => {
    const ac = new AbortController();
    setData(null);
    setOpen(false);
    const ids = idsKey.split(',').filter(Boolean);
    (async () => {
      try {
        const res = await api.routeSummary.getLocationSuggestions(
          {
            groupingIds: ids.map((id) => (/^\d+$/.test(id) ? Number(id) : id)),
            startDate,
            endDate,
          },
          { signal: ac.signal }
        );
        if (!ac.signal.aborted) setData(res.data);
      } catch {
        // silent: the indicator is a hint beside the figures, never an error of the summary
      }
    })();
    return () => ac.abort();
  }, [idsKey, startDate, endDate]);

  const s = data?.enabled ? data.summary : null;
  if (!data || !s || s.tickets + s.groupings === 0) return null;
  return (
    <>
      <div className="px-4 sm:px-6 py-2 border-b border-slate-200 bg-slate-50 text-[11px] sm:text-xs flex items-start gap-2">
        <FaMapMarkerAlt
          className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600"
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          className="min-w-0 text-left break-words font-medium text-primary-700 hover:text-primary-900 underline underline-offset-2"
        >
          {indicatorText(s.tickets, s.groupings)}
        </button>
      </div>
      {open && (
        <SuggestionsDialog
          data={data}
          label={label}
          period={{ startDate, endDate }}
          canAmend={canAmend}
          onClose={close}
        />
      )}
    </>
  );
}
